// YOLOv9 end2end plate detector pre/post-processing.
// Contract (verified from open-image-models source + ONNX graph, PLAN §2):
//   input  `images` float32 NCHW [1,3,384,384], RGB /255, letterbox pad rgb(114)
//   output `output0` float32 [N,7] = [batch, x1, y1, x2, y2, class_id, score]
//   (coords in letterboxed space; NMS already applied in-graph)

import type { Detection, Rect } from '../types';
import { TUNING } from '../types';
import type { Letterbox } from './raster';

/** Map raw output rows back to (crop-)frame pixels and filter by score. */
export function decodeDetections(
  output: Float32Array,
  lb: Letterbox,
  frameW: number,
  frameH: number,
  scoreThreshold: number = TUNING.dMin,
): Detection[] {
  const out: Detection[] = [];
  for (let i = 0; i + 7 <= output.length; i += 7) {
    const score = output[i + 6] ?? 0;
    if (score < scoreThreshold) continue;
    const x1 = ((output[i + 1] ?? 0) - lb.dw) / lb.ratio;
    const y1 = ((output[i + 2] ?? 0) - lb.dh) / lb.ratio;
    const x2 = ((output[i + 3] ?? 0) - lb.dw) / lb.ratio;
    const y2 = ((output[i + 4] ?? 0) - lb.dh) / lb.ratio;
    const x = Math.max(0, Math.min(x1, frameW));
    const y = Math.max(0, Math.min(y1, frameH));
    const box: Rect = {
      x,
      y,
      w: Math.max(0, Math.min(x2, frameW) - x),
      h: Math.max(0, Math.min(y2, frameH) - y),
    };
    if (box.w < 4 || box.h < 2) continue;
    out.push({ box, score });
  }
  return out;
}

export function iou(a: Rect, b: Rect): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a.w * a.h + b.w * b.h - inter;
  return union > 0 ? inter / union : 0;
}

/**
 * Dominant-candidate selection (spec §4): score × area × center bias, sticky to
 * the currently tracked box so a second car entering the frame doesn't steal the
 * vote mid-lock.
 */
export function pickDominant(
  dets: Detection[],
  frameW: number,
  frameH: number,
  lastBox: Rect | null,
): Detection | null {
  if (dets.length === 0) return null;
  if (lastBox) {
    let best: Detection | null = null;
    let bestIou: number = TUNING.stickyIou;
    for (const d of dets) {
      const v = iou(d.box, lastBox);
      if (v >= bestIou) {
        bestIou = v;
        best = d;
      }
    }
    if (best) return best;
  }
  const cx = frameW / 2;
  const cy = frameH / 2;
  const maxDist = Math.hypot(cx, cy);
  let best = dets[0] ?? null;
  let bestScore = -1;
  for (const d of dets) {
    const bx = d.box.x + d.box.w / 2;
    const by = d.box.y + d.box.h / 2;
    const centerBias = 1 - Math.hypot(bx - cx, by - cy) / maxDist; // 0..1
    const areaNorm = Math.min(1, (d.box.w * d.box.h) / (frameW * frameH * 0.05));
    const s = d.score * (0.5 + 0.25 * centerBias + 0.25 * areaNorm);
    if (s > bestScore) {
      bestScore = s;
      best = d;
    }
  }
  return best;
}
